import api, { ApiResponse } from './api';
import type {
  SplitCreateGroupSelection,
  SplitCreateResult,
  SplitPlanResponse,
} from '@safetyhub/shared';

/**
 * 분할 생성(1점검 → N문서) 웹 API — 결정 2026-09-01 「Composer 분할 생성」 ③.
 *
 * `lib/reports.ts`가 아니라 별 모듈인 이유: 그 모듈은 여러 spec이 `vi.mock` 팩토리로 통째
 * 대체하고 있어, export를 하나 늘리면 그 모듈을 mock하는 무관한 spec들이 전부
 * `No "X" export is defined on the mock`으로 죽는다([[feedback-pure-predicate-must-not-live-in-a-vi-mocked-module]]).
 * 분할은 자체 엔드포인트 2개뿐이라 붙일 이유도 없다.
 */

/**
 * 계획 조회 — 그룹 선택 모달이 화면을 그리기 전에 부른다.
 * POST /reports/composed/split-plan
 *
 * ⚠️ `plan: null`은 오류가 아니라 **"이 템플릿·컨텍스트는 분할하지 않는다"**이다(정책 미선언·
 * 미등록 provider·대상 행 0건이 전부 여기로 접힌다). 호출부는 그 경우 그룹 선택 없이 종전
 * 1문서 흐름으로 가야 한다 — 정책 오타 하나로 작성이 통째로 막히면 안 된다.
 */
export async function getSplitPlan(params: {
  companyId: string;
  siteId: string;
  templateKey: string;
  inspectionIds: string[];
}): Promise<SplitPlanResponse> {
  const response = await api.post<ApiResponse<SplitPlanResponse>>(
    '/reports/composed/split-plan',
    params,
  );
  return response.data.data ?? { plan: null, existing: [] };
}

/**
 * 분할 생성 — 선택된 그룹마다 DRAFT 1건.
 * POST /reports/composed/split
 *
 * ⚠️ 통합(combined) 모드는 이 경로로 보내지 않는다 — "분할하지 않음 = 1건"이라 기존
 * `POST /reports/composed`(작성 페이지 진입) 그대로다(결정 §③-1).
 *
 * ⚠️ `content`에 본문 **행 배열**을 실으면 안 된다. 합성이 `resolve 산출 → 작성값 덮어쓰기`
 * 순서라, 분할 소스에서 온 행을 여기 실으면 서버의 행 제한을 덮어써 N건이 전부 같아진다.
 * 여기 담는 것은 N건 **공통** 진입 기본값(서명 방식·교육 기간 등, 결정 §⑤)뿐이다.
 */
export async function createSplitReports(params: {
  companyId: string;
  siteId: string;
  templateKey: string;
  inspectionIds: string[];
  title?: string;
  content?: Record<string, unknown>;
  groups: SplitCreateGroupSelection[];
}): Promise<SplitCreateResult> {
  const response = await api.post<ApiResponse<SplitCreateResult>>(
    '/reports/composed/split',
    params,
  );
  return response.data.data ?? { created: [], skipped: [] };
}
